/* ==========================================================================
   ATN Homepage V1.0 — Section 01: Navigation Header
   Spec reference: 01_Section.docx §47 (JS), §48 (analytics)

   Handles: desktop dropdown toggles + keyboard, hover-intent close delay,
   mobile drawer open/close, focus trap, scroll lock, submenu accordions,
   search-panel toggle, sticky scrolled state, and optional analytics.

   Cart is handled entirely by the theme's own cart-drawer.js via the
   id="cart-icon" / id="cart-icon-bubble" contract, so it is not touched here.
   ========================================================================== */

(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // How long a desktop dropdown stays open after the pointer leaves (ms)
  var HOVER_CLOSE_DELAY = 250;

  function deviceType() {
    return window.innerWidth <= 1180 ? 'mobile' : 'desktop';
  }

  function sectionIdFrom(el) {
    var wrapper = el.closest('[id^="shopify-section-"]');
    return wrapper ? wrapper.id.replace('shopify-section-', '') : '';
  }

  function ATNNavigation(root) {
    this.root = root;
    this.analytics = root.dataset.analytics === 'true';
    this.sticky = root.dataset.sticky === 'true';

    this.desktopMenu = root.querySelector('.atn-navigation__desktop-menu');
    this.mobileMenu = root.querySelector('[data-atn-mobile-menu]');
    this.searchPanel = root.querySelector('[data-atn-search-panel]');
    this.menuButton = root.querySelector('[data-atn-menu-open]');

    this.lastFocused = null;

    this.bindDesktopDropdowns();
    this.initMenuScroller();
    this.bindMobileMenu();
    this.bindSubmenus();
    this.bindSearch();
    this.bindAnalytics();
    if (this.sticky) this.bindSticky();
  }

  /* ---- Analytics ------------------------------------------------------- */

  ATNNavigation.prototype.track = function (eventName, params) {
    if (!this.analytics || !eventName) return;

    var payload = Object.assign(
      {
        device_type: deviceType(),
        section_id: sectionIdFrom(this.root),
        current_page: window.location.pathname
      },
      params || {}
    );

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(Object.assign({ event: eventName }, payload));
    }
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, payload);
    }
  };

  /* ---- Scrollable desktop menu ----------------------------------------
     On narrow desktops the menu is wider than its grid track, which used to
     make it overlap the logo. When that happens we clip the strip, show a
     small arrow on each side, and reposition dropdowns as fixed elements so
     they are not cut off by the clipping.
     -------------------------------------------------------------------- */

  ATNNavigation.prototype.initMenuScroller = function () {
    var self = this;
    if (!this.desktopMenu) return;

    this.viewport = this.desktopMenu.querySelector('[data-atn-nav-viewport]');
    this.list = this.desktopMenu.querySelector('.atn-navigation__list');
    this.prevArrow = this.desktopMenu.querySelector('[data-atn-nav-scroll="prev"]');
    this.nextArrow = this.desktopMenu.querySelector('[data-atn-nav-scroll="next"]');
    if (!this.viewport || !this.list) return;

    [this.prevArrow, this.nextArrow].forEach(function (arrow) {
      if (!arrow) return;
      arrow.addEventListener('click', function () {
        var step = Math.max(140, Math.round(self.viewport.clientWidth * 0.6));
        var dir = arrow.getAttribute('data-atn-nav-scroll') === 'prev' ? -1 : 1;
        self.closeAllDropdowns();
        self.viewport.scrollBy({ left: step * dir, behavior: 'smooth' });
      });
    });

    this.viewport.addEventListener('scroll', function () {
      self.updateArrows();
      self.closeAllDropdowns();
    });

    // Keyboard focus inside the strip must pull the item into view
    this.list.addEventListener('focusin', function (event) {
      if (!self.root.classList.contains('is-scrollable')) return;
      var item = event.target.closest('.atn-navigation__item');
      if (item && item.scrollIntoView) {
        item.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    });

    // Position dropdowns while the strip is clipped
    this.list
      .querySelectorAll('.atn-navigation__item--has-dropdown')
      .forEach(function (item) {
        item.addEventListener('mouseenter', function () {
          self.placeDropdown(item);
        });
        item.addEventListener('focusin', function () {
          self.placeDropdown(item);
        });
      });

    this.measureMenu();

    var onResize = function () {
      window.clearTimeout(self.menuResizeTimer);
      self.menuResizeTimer = window.setTimeout(function () {
        self.measureMenu();
      }, 120);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', function () {
      if (self.root.classList.contains('is-scrollable')) self.closeAllDropdowns();
    }, { passive: true });
  };

  ATNNavigation.prototype.measureMenu = function () {
    if (!this.viewport || !this.list) return;

    /* Measure with the scrollable styles applied. Without them the list uses
       justify-content: flex-end, so the overflow spills to the left and
       scrollWidth does not report it. */
    this.root.classList.add('is-scrollable');

    if (this.viewport.scrollWidth <= this.viewport.clientWidth + 1) {
      this.root.classList.remove('is-scrollable');
      this.viewport.scrollLeft = 0;
      return;
    }

    this.updateArrows();
  };

  ATNNavigation.prototype.updateArrows = function () {
    if (!this.viewport) return;
    var left = this.viewport.scrollLeft;
    var max = this.viewport.scrollWidth - this.viewport.clientWidth;
    if (this.prevArrow) this.prevArrow.classList.toggle('is-disabled', left <= 1);
    if (this.nextArrow) this.nextArrow.classList.toggle('is-disabled', left >= max - 1);
  };

  ATNNavigation.prototype.placeDropdown = function (item) {
    if (!this.root.classList.contains('is-scrollable')) return;
    var dropdown = item.querySelector('.atn-navigation__dropdown');
    if (!dropdown) return;

    var row = item.getBoundingClientRect();
    var origin = { top: 0, left: 0 };

    // .is-stuck sets will-change: transform, which makes the nav the
    // containing block for fixed children, so offsets are relative to it.
    if (this.root.classList.contains('is-stuck')) {
      var navBox = this.root.getBoundingClientRect();
      origin.top = navBox.top;
      origin.left = navBox.left;
    }

    dropdown.style.top = (row.bottom - origin.top + 6) + 'px';

    var width = dropdown.offsetWidth || 240;
    var left = row.left - origin.left;
    var maxLeft = document.documentElement.clientWidth - origin.left - width - 12;
    if (left > maxLeft) left = maxLeft;
    if (left < 12) left = 12;
    dropdown.style.left = left + 'px';
  };

  /* ---- Desktop dropdowns ---------------------------------------------- */

  ATNNavigation.prototype.bindDesktopDropdowns = function () {
    var self = this;
    if (!this.desktopMenu) return;

    this.desktopMenu
      .querySelectorAll('[data-atn-dropdown-toggle]')
      .forEach(function (toggle) {
        var item = toggle.closest('.atn-navigation__item');
        var dropdown = item ? item.querySelector('.atn-navigation__dropdown') : null;
        if (!dropdown) return;

        toggle.addEventListener('click', function () {
          var open = toggle.getAttribute('aria-expanded') === 'true';
          self.closeAllDropdowns();
          if (!open) {
            toggle.setAttribute('aria-expanded', 'true');
            dropdown.classList.add('is-open');
            self.track('header_dropdown_open', {
              nav_label: item.querySelector('.atn-navigation__link')
                ? item.querySelector('.atn-navigation__link').textContent.trim()
                : '',
              menu_level: 'top'
            });
          }
        });
      });

    // Hover intent: keep the dropdown open briefly after the pointer leaves,
    // so moving from the link into the dropdown never drops it.
    this.desktopMenu
      .querySelectorAll('.atn-navigation__item--has-dropdown')
      .forEach(function (item) {
        var timer;
        item.addEventListener('mouseenter', function () {
          window.clearTimeout(timer);
          self.desktopMenu
            .querySelectorAll('.atn-navigation__item.is-hover')
            .forEach(function (other) {
              if (other !== item) other.classList.remove('is-hover');
            });
          item.classList.add('is-hover');
        });
        item.addEventListener('mouseleave', function () {
          timer = window.setTimeout(function () {
            item.classList.remove('is-hover');
          }, HOVER_CLOSE_DELAY);
        });
      });

    // Escape closes any open dropdown, returns focus to its toggle
    this.desktopMenu.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var openToggle = self.desktopMenu.querySelector(
        '[data-atn-dropdown-toggle][aria-expanded="true"]'
      );
      self.closeAllDropdowns();
      if (openToggle) openToggle.focus();
    });

    // Click outside closes dropdowns
    document.addEventListener('click', function (event) {
      if (!self.desktopMenu.contains(event.target)) {
        self.closeAllDropdowns();
      }
    });
  };

  ATNNavigation.prototype.closeAllDropdowns = function () {
    if (!this.desktopMenu) return;
    this.desktopMenu
      .querySelectorAll('.atn-navigation__item.is-hover')
      .forEach(function (i) {
        i.classList.remove('is-hover');
      });
    this.desktopMenu
      .querySelectorAll('.atn-navigation__dropdown.is-open')
      .forEach(function (d) {
        d.classList.remove('is-open');
      });
    this.desktopMenu
      .querySelectorAll('[data-atn-dropdown-toggle][aria-expanded="true"]')
      .forEach(function (t) {
        t.setAttribute('aria-expanded', 'false');
      });
  };

  /* ---- Mobile drawer --------------------------------------------------- */

  ATNNavigation.prototype.bindMobileMenu = function () {
    var self = this;
    if (!this.mobileMenu || !this.menuButton) return;

    this.menuButton.addEventListener('click', function () {
      self.openMobileMenu();
    });

    this.mobileMenu
      .querySelectorAll('[data-atn-menu-close]')
      .forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.closeMobileMenu();
        });
      });

    this.mobileMenu.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        self.closeMobileMenu();
      } else if (event.key === 'Tab') {
        self.trapFocus(event);
      }
    });

    // Close after selecting any link
    this.mobileMenu
      .querySelectorAll('a[href]')
      .forEach(function (link) {
        link.addEventListener('click', function () {
          self.closeMobileMenu();
        });
      });
  };

  ATNNavigation.prototype.openMobileMenu = function () {
    var self = this;
    this.lastFocused = document.activeElement;
    this.mobileMenu.hidden = false;
    // allow the browser to paint before transitioning
    requestAnimationFrame(function () {
      self.mobileMenu.classList.add('is-open');
    });
    this.menuButton.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    var firstFocusable = this.mobileMenu.querySelector(FOCUSABLE);
    if (firstFocusable) firstFocusable.focus();

    this.track('header_mobile_menu_open', {});
  };

  ATNNavigation.prototype.closeMobileMenu = function () {
    var self = this;
    this.mobileMenu.classList.remove('is-open');
    this.menuButton.setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';

    var onEnd = function () {
      self.mobileMenu.hidden = true;
      self.mobileMenu.removeEventListener('transitionend', onEnd);
    };
    this.mobileMenu.addEventListener('transitionend', onEnd);
    // Fallback in case transitionend never fires
    setTimeout(onEnd, 320);

    if (this.lastFocused) this.lastFocused.focus();
  };

  ATNNavigation.prototype.trapFocus = function (event) {
    var focusables = Array.prototype.slice
      .call(this.mobileMenu.querySelectorAll(FOCUSABLE))
      .filter(function (el) {
        return el.offsetParent !== null;
      });
    if (!focusables.length) return;

    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ---- Mobile submenu accordions -------------------------------------- */

  ATNNavigation.prototype.bindSubmenus = function () {
    if (!this.mobileMenu) return;

    this.mobileMenu
      .querySelectorAll('[data-atn-submenu-toggle]')
      .forEach(function (toggle) {
        // The submenu is the sibling <ul> right after the toggle's row,
        // so this works for both child (__submenu) and grandchild
        // (__subsubmenu) levels without hard-coding a class.
        var row = toggle.closest(
          '.atn-mobile-menu__item-row, .atn-mobile-menu__subitem-row'
        );
        var container = row ? row.parentElement : toggle.parentElement;
        var submenu = container
          ? container.querySelector(
              ':scope > .atn-mobile-menu__submenu, :scope > .atn-mobile-menu__subsubmenu'
            )
          : null;
        if (!submenu) return;

        toggle.addEventListener('click', function (event) {
          event.stopPropagation();
          var open = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', String(!open));
          submenu.hidden = open;
        });
      });
  };

  /* ---- Search panel ---------------------------------------------------- */

  ATNNavigation.prototype.bindSearch = function () {
    var self = this;
    var opener = this.root.querySelector('[data-atn-search-open]');
    if (!opener || !this.searchPanel) return;

    var closer = this.searchPanel.querySelector('[data-atn-search-close]');

    var open = function () {
      self.searchPanel.hidden = false;
      opener.setAttribute('aria-expanded', 'true');
      var input = self.searchPanel.querySelector('input[type="search"], input[type="text"]');
      if (input) input.focus();
      self.track('header_search_open', {});
    };

    var close = function () {
      self.searchPanel.hidden = true;
      opener.setAttribute('aria-expanded', 'false');
      opener.focus();
    };

    opener.addEventListener('click', function () {
      if (self.searchPanel.hidden) open();
      else close();
    });

    if (closer) closer.addEventListener('click', close);

    this.searchPanel.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') close();
    });

    document.addEventListener('click', function (event) {
      if (
        !self.searchPanel.hidden &&
        !self.searchPanel.contains(event.target) &&
        !opener.contains(event.target)
      ) {
        close();
      }
    });
  };

  /* ---- Analytics on links --------------------------------------------- */

  ATNNavigation.prototype.bindAnalytics = function () {
    var self = this;
    if (!this.analytics) return;

    this.root.addEventListener('click', function (event) {
      var link = event.target.closest('[data-atn-nav-link]');
      if (!link) return;

      var label = (link.textContent || '').trim();
      var isMembership = /member/i.test(link.getAttribute('href') || '') || /member/i.test(label);
      var level = link.classList.contains('atn-navigation__dropdown-link')
        || link.classList.contains('atn-navigation__dropdown-link--sub')
        || link.classList.contains('atn-mobile-menu__submenu-link')
        ? 'child'
        : 'top';

      self.track('header_navigation_click', {
        nav_label: label,
        menu_level: level,
        destination_url: link.getAttribute('href') || ''
      });

      if (isMembership) {
        self.track('header_membership_click', {
          nav_label: label,
          destination_url: link.getAttribute('href') || ''
        });
      }
    });
  };

  /* ---- Sticky (fixed-based, robust against body{overflow-x:hidden}) ----- */
  /* CSS `position: sticky` fails when any ancestor has overflow-x: hidden,
     which this theme sets on <body>. So we switch the nav to position: fixed
     past a scroll threshold and insert a spacer to prevent content jump.

     `is-scrolled` (logo shrink + shadow) is tied to `is-stuck` so the logo
     never resizes while the header is still in its normal flow position. */

  ATNNavigation.prototype.bindSticky = function () {
    var self = this;
    var ticking = false;

    // Spacer keeps layout height when the nav becomes fixed.
    this.spacer = document.createElement('div');
    this.spacer.className = 'atn-navigation__spacer';
    this.spacer.setAttribute('aria-hidden', 'true');
    this.spacer.hidden = true;
    this.root.parentNode.insertBefore(this.spacer, this.root.nextSibling);

    // Remember the nav's natural offset from the top of the document once,
    // measured while it is NOT stuck. Recomputed on resize.
    this.naturalTop = 0;
    var measureNaturalTop = function () {
      var wasStuck = self.root.classList.contains('is-stuck');
      if (wasStuck) {
        // temporarily un-stick to measure the true flow position
        self.root.classList.remove('is-stuck');
        self.spacer.hidden = false;
      }
      self.naturalTop = self.spacer.hidden
        ? self.root.getBoundingClientRect().top + window.scrollY
        : self.spacer.getBoundingClientRect().top + window.scrollY;
      if (wasStuck) {
        self.root.classList.add('is-stuck');
        self.spacer.hidden = false;
      }
    };

    var stuck = false;
    // Small hysteresis gap so the header doesn't flicker on/off right at the
    // boundary — it engages a little past the nav and releases a little before.
    var ENGAGE_OFFSET = 8;

    var update = function () {
      var y = window.scrollY;

      if (!stuck && y > self.naturalTop + ENGAGE_OFFSET) {
        self.spacer.style.height = self.root.offsetHeight + 'px';
        self.spacer.hidden = false;
        // stick + shrink together, in the same frame
        self.root.classList.add('is-stuck');
        self.root.classList.add('is-scrolled');
        stuck = true;
      } else if (stuck && y <= self.naturalTop) {
        self.root.classList.remove('is-stuck');
        self.root.classList.remove('is-scrolled');
        self.spacer.hidden = true;
        stuck = false;
      }

      ticking = false;
    };

    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );

    window.addEventListener('resize', function () {
      measureNaturalTop();
      if (stuck) self.spacer.style.height = self.root.offsetHeight + 'px';
    });

    // Initial measure + state
    measureNaturalTop();
    update();
  };

  /* ---- Boot ------------------------------------------------------------ */

  function init(scope) {
    (scope || document)
      .querySelectorAll('[data-atn-nav-root]')
      .forEach(function (root) {
        if (root.dataset.atnNavBound === 'true') return;
        root.dataset.atnNavBound = 'true';
        new ATNNavigation(root);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();